import React, { useState } from 'react';
import { Star, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../mock';
import { SITE } from '../lib/site';
import { Button } from './ui/button';

const GoogleG = (props) => (
  <svg viewBox="0 0 48 48" aria-hidden {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 6.2 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 6.2 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.8-5.3l-6.4-5.3C29.4 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.3l6.4 5.3c-.4.4 6.9-5 6.9-14.6 0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const ReviewCard = ({ t, expanded, onToggle }) => {
  const initials = t.name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isLong = t.text.length > 220;

  return (
    <article
      data-testid={`review-card-${t.id}`}
      className="relative flex flex-col bg-white/[0.04] hover:bg-white/[0.06] border border-white/10 hover:border-purple-400/30 rounded-2xl p-5 backdrop-blur-md transition-all duration-300"
    >
      {/* Header row: stars + Google G */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <GoogleG className="h-5 w-5" />
      </div>

      {/* Body */}
      <p className={`text-gray-200 leading-relaxed text-sm mb-4 ${isLong && !expanded ? 'line-clamp-4' : ''}`}>
        {t.text}
      </p>

      {isLong && (
        <button
          onClick={onToggle}
          className="text-xs font-semibold text-purple-300 hover:text-purple-200 self-start mb-3"
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      {/* Author */}
      <div className="mt-auto flex items-center gap-3 pt-3 border-t border-white/10">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
          {initials}
        </div>
        <div className="min-w-0">
          <div data-testid="review-name" className="font-semibold text-white text-sm truncate">{t.name}</div>
          <div className="text-[11px] text-gray-400 truncate">{t.role}</div>
        </div>
      </div>
    </article>
  );
};

export const Testimonials = () => {
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const perPage = 3;
  const totalPages = Math.ceil(testimonials.length / perPage);
  const visible = testimonials.slice(page * perPage, page * perPage + perPage);

  const next = () => {
    setExpandedId(null);
    setPage((p) => (p + 1) % totalPages);
  };
  const prev = () => {
    setExpandedId(null);
    setPage((p) => (p - 1 + totalPages) % totalPages);
  };

  return (
    <section className="py-14 bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-80 h-80 bg-purple-900/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-orange-900/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 bg-white/5 border border-white/10 rounded-full">
                <GoogleG className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300">
                  Loved on Google
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                Success Stories from{' '}
                <span className="bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">
                  MatchFrame Alumni
                </span>
              </h2>
              <div className="flex items-center gap-2 mt-2 text-gray-300 text-sm">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-yellow-300 font-semibold">5 / 5</span>
                <span className="text-gray-500">·</span>
                <span className="text-xs">Reviews from Google Maps</span>
              </div>
            </div>

            <a
              href={SITE.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="testimonials-google-cta"
              className="shrink-0"
            >
              <Button className="bg-white text-slate-900 hover:bg-gray-100 px-5 py-4 rounded-full font-semibold text-sm shadow-lg">
                <GoogleG className="h-4 w-4 mr-2" />
                Read all on Google
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Button>
            </a>
          </div>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {visible.map((t) => (
              <ReviewCard
                key={t.id}
                t={t}
                expanded={expandedId === t.id}
                onToggle={() => setExpandedId((id) => (id === t.id ? null : t.id))}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous reviews"
              data-testid="testimonials-prev"
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1.5">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setExpandedId(null); setPage(i); }}
                  aria-label={`Go to page ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === page ? 'w-8 bg-purple-400' : 'w-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              aria-label="Next reviews"
              data-testid="testimonials-next"
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-all"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
